import {VNode} from 'snabbdom';

export interface InputState {
  /**
   * State used to handle Safari's odd behavior of not triggering a change
   * when we mutate the value of the input with formatting (adding commas for
   * numbers). We use this state to force a change event to be triggered on blur
   * if the input value has changed.
   *
   * Ideally Safari will fix this issue and we can remove this state and all code
   * that references it.
   */
  nextBlurShouldTriggerChange: boolean;
  textWidth: number | null;
  value: string;
}

export enum InputType {
  Text = `text`,
  Password = `password`,
  Number = `number`,
  TextArea = `textarea`,
}

export enum InputSize {
  Small = `small`,
  Medium = `medium`,
  Large = `large`,
}

export enum InputTheme {
  Default = `default`,
  White = `white`,
}

export enum Necessary {
  Default = ``,
  Optional = `optional`,
  Recommended = `recommended`,
  Required = `required`,
}

export enum ValidationStatus {
  Default = ``,
  Valid = `valid`,
  Invalid = `invalid`,
}

export enum TextAlign {
  Start = `start`,
  Center = `center`,
  End = `end`,
}

export enum AutocompleteType {
  CurrentPassword = `current-password`,
  NewPassword = `new-password`,
  Email = `email`,
  InviteUsersAutocompleteOff = `invite-users-autocomplete-off`,
  Off = `off`,
}

// Replace with React type after conversion?
type GenericEvent<T> = Event & {
  target: T;
};
type GenericInputEvent<T> = InputEvent & {
  target: T;
};
type GenericKeyboardEvent<T> = KeyboardEvent & {
  target: T;
};
type GenericClipboardEvent<T> = ClipboardEvent & {
  target: T;
};
type GenericFocusEvent<T> = FocusEvent & {
  target: T;
};
type GenericPointerEvent<T> = PointerEvent & {
  target: T;
};

export type InputFocusEvent = GenericFocusEvent<HTMLInputElement | HTMLTextAreaElement>;
export type InputKeyboardEvent = GenericKeyboardEvent<HTMLInputElement | HTMLTextAreaElement>;
export type InputChangeEvent = GenericEvent<HTMLInputElement | HTMLTextAreaElement>;
export type InputInputEvent = GenericInputEvent<HTMLInputElement | HTMLTextAreaElement>;
export type InputClipboardEvent = GenericClipboardEvent<HTMLInputElement | HTMLTextAreaElement>;
export type InputPointerEvent = GenericPointerEvent<HTMLInputElement | HTMLTextAreaElement>;

export const inputParamsConfig = {
  // enum: Object.values(AutocompleteType)
  autocomplete: {
    type: String,
  },

  ariaLabel: {
    type: String,
  },

  hideRightIcon: Boolean,

  // If set, automatically focuses on the input field
  autoFocus: {
    type: Boolean,
  },

  notFocusable: Boolean,
  multilinePrefix: Boolean,

  // Hide focus styling, typically because a parent is styling the focus state
  hideFocusStyling: {
    type: Boolean,
  },

  // Whether input is disabled
  disabled: {
    type: Boolean,
  },

  // Whether input is readonly
  readonly: {
    type: Boolean,
  },

  // Id of input field, maps to field label for accessibility
  fieldId: {
    type: String,
  },

  // Name of the input field, maps to field label
  fieldName: {
    type: String,
  },

  // Left icon that goes inline with the input
  icon: {
    type: String,
  },

  // Label on top that describes the input field
  label: {
    type: String,
  },

  // Show clear input button when there's no input
  showClear: {
    type: Boolean,
  },

  // Show clear input button regardless if there's input or not
  alwaysShowClear: {
    type: Boolean,
  },

  // Placeholder text for input
  placeholder: {
    type: String,
  },

  // Mono-width text at the beginning of the input
  preText: {
    type: String,
  },

  // Feedback for user to perform action when the input is focused. e.g. "Press enter to search"
  actionText: {
    type: String,
  },

  // If set, action-text will always be visible, even when the input is empty
  alwaysShowActionText: {
    type: Boolean,
  },

  // enum: Object.values(InputType)
  type: {
    type: String,
  },

  // enum: Object.values(InputSize)
  size: {
    type: String,
  },

  // enum: Object.values(Necessary)
  necessary: {
    type: String,
  },

  // enum: Object.values(ValidationStatus)
  validationStatus: {
    type: String,
  },

  // Error message to show when invalid validation status
  errorMessage: {
    type: String,
  },

  // Maximum number of characters allowed
  maxlength: {
    type: Number,
  },

  // If set, an "info" icon will be shown next to the label. This value will be shown as a tooltip on hover
  helpText: {
    type: String,
  },

  // If set, we will show this string next to the label. This is useful to give the user additional context such as which SDK verion a field is supported in
  extraInfo: {
    type: String,
  },

  // use "string" to avoid default value for number type
  min: String,

  max: String,
  step: String,

  // Minimum width of the internal input element in pixels. This should be set when there's
  // a prefixRenderer (which takes up horizontal space) to prevent the input element
  // from shrinking below some minimum width.
  minInputWidth: {
    type: Number,
  },

  // Used for fields where user will most likely replace the whole input value instead of editing portions of the input
  selectOnFocus: {
    type: Boolean,
  },

  // enum: Object.values(TextAlign)
  textAlign: {
    type: String,
  },

  // enum: Object.values(InputTheme)
  theme: {
    type: String,
  },

  showNumericInputControls: {
    type: Boolean,
  },

  onBlur: Function,
  onChange: Function,
  onClear: Function,
  onClick: Function,
  onFocus: Function,
  onInput: Function,
  onKeydown: Function,
  onKeyup: Function,
  onPaste: Function,
  prefixRenderer: Function,
  suffixRenderer: Function,
};

export type InputParams = {
  actionText?: string;
  alwaysShowActionText?: boolean;
  alwaysShowClear?: boolean;
  ariaLabel?: string;
  autoFocus?: boolean;
  autocomplete?: AutocompleteType;
  disabled?: boolean;
  errorMessage?: string;
  extraInfo?: string;
  fieldId?: string;
  fieldName?: string;
  helpText?: string;
  hideFocusStyling?: boolean;
  hideRightIcon?: boolean;
  icon?: string;
  label?: string;
  max?: string; // use "string" to avoid default value for number type
  maxlength?: number;
  min?: string; // use "string" to avoid default value for number type
  minInputWidth?: number;
  multilinePrefix?: boolean;
  necessary?: string;
  notFocusable?: boolean;
  onBlur?: (ev: InputFocusEvent) => void;
  /**
   * Prefer to use `value`. Do NOT use `ev.target.value`. That value is a display
   * value and not the true backing value.
   * @param value
   * @param ev Do NOT use this for `ev.target.value`. Use the `value` params
   * @returns
   */
  onChange?: (value: string, ev: InputChangeEvent) => void;
  onClear?: (ev: Event) => void;
  onClick?: (ev: InputPointerEvent) => void;
  onFocus?: (ev: InputFocusEvent) => void;
  /**
   * Prefer to use `value`. Do NOT use `ev.target.value`. That value is a display
   * value and not the true backing value.
   * @param value
   * @param ev Do NOT use this for `ev.target.value`. Use the `value` params
   * @returns
   */
  onInput?: (value: string, ev: InputInputEvent) => void;
  onKeydown?: (ev: InputKeyboardEvent) => void;
  onKeyup?: (ev: InputKeyboardEvent) => void;
  onPaste?: (ev: InputClipboardEvent) => void;
  placeholder?: string;
  /** renders content before the input element and after any icons */
  prefixRenderer?: () => VNode | VNode[];
  preText?: string;
  readonly?: boolean;
  selectOnFocus?: boolean;
  showClear?: boolean;
  showNumericInputControls?: boolean;
  size?: string;
  step?: string;
  /** renders content after the input element */
  suffixRenderer?: () => VNode | VNode[];
  textAlign?: TextAlign;
  theme?: string;
  type?: string;
  validationStatus?: string;
};
